<template>
  <div
    :id="id"
    :aria-label="ariaLabel"
    :aria-labelledby="labelId"
    class="base-radio-group"
  >
    <label
      v-for="option in options"
      :key="option.value"
      class="base-radio"
    >
      <input
        :checked="modelValue === option.value"
        class="base-radio-input"
        :disabled="disabled || option.disabled"
        :name="name"
        :required="required"
        type="radio"
        :value="option.value"
        @change="handleChange(option.value)"
      />
      <span class="base-radio-label">{{ option.label }}</span>
    </label>
  </div>
</template>

<script setup lang="ts">
/**
 * Radio button option structure
 */
export interface RadioOption {
  /** Value emitted when this option is selected */
  value: string | number;
  /** Display text shown next to radio button */
  label: string;
  /**
   * Disables this specific option
   * @default false
   */
  disabled?: boolean;
}

/**
 * Radio button group component with v-model support
 *
 * Provides a group of styled radio buttons for single-choice selection.
 * Supports individual option disabling and group-level configuration.
 *
 * @example Basic radio group
 * ```vue
 * <BaseRadioGroup
 *   v-model="size"
 *   name="product-size"
 *   :options="[
 *     { value: 'sm', label: 'Small' },
 *     { value: 'md', label: 'Medium' },
 *     { value: 'lg', label: 'Large' }
 *   ]"
 * />
 * ```
 *
 * @example With disabled options
 * ```vue
 * <BaseRadioGroup
 *   v-model="plan"
 *   name="subscription"
 *   :options="[
 *     { value: 'free', label: 'Free Plan' },
 *     { value: 'pro', label: 'Pro Plan' },
 *     { value: 'enterprise', label: 'Enterprise', disabled: true }
 *   ]"
 * />
 * ```
 *
 * @example With accessibility
 * ```vue
 * <BaseRadioGroup
 *   v-model="priority"
 *   name="task-priority"
 *   :options="priorityOptions"
 *   aria-label="Task priority level"
 *   required
 * />
 * ```
 */
export interface BaseRadioGroupProps {
  /** Currently selected value (use with v-model) */
  modelValue: string | number;

  /** Array of radio button options to display */
  options: RadioOption[];

  /** Name attribute for radio group (groups radio buttons together) */
  name: string;

  /** Unique element ID for the radio group container */
  id?: string;

  /** ID of element labeling this radio group */
  labelId?: string;

  /** Accessible label for screen readers (use when visual label not present) */
  ariaLabel?: string;

  /**
   * Disables all radio buttons in the group
   * Individual options can also be disabled via RadioOption.disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Marks radio group as required for form validation
   * @default false
   */
  required?: boolean;
}

withDefaults(defineProps<BaseRadioGroupProps>(), {
  disabled: false,
  required: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: string | number];
}>();

const handleChange = (value: string | number) => {
  emit('update:modelValue', value);
};
</script>

<style>
.base-radio-group {
  display: flex;
  flex-direction: column;
  gap: var(--gap-sm);
  flex-wrap: wrap;

  .base-radio {
    display: inline-flex;
    align-items: center;
    gap: var(--gap-xs);
    cursor: pointer;
    user-select: none;
  }

  .base-radio:has(.base-radio-input:disabled) {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .base-radio-input {
    width: var(--size-radio-size);
    height: var(--size-radio-size);
    margin: 0;
    cursor: inherit;
  }

  .base-radio-label {
    font-size: var(--font-md);
    line-height: 1.5;
  }
}
</style>
